import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { X, Grid3X3, List, MapPin, Home, Wallet, Ruler } from "lucide-react";

const CATEGORIES = [
  { value: "maison", label: "Maison" },
  { value: "appartement", label: "Appartement" },
  { value: "terrain", label: "Terrain" },
  { value: "garage", label: "Garage" },
  { value: "parking", label: "Parking" },
  { value: "commercial", label: "Local commercial" },
];
const PRICE_MAX = 2000000;
const SURFACE_MAX = 500;
const DEFAULT_FILTERS = { property_type: "", category: "", city: "", min_price: 0, max_price: PRICE_MAX, min_surface: 0, max_surface: SURFACE_MAX };

export const CatalogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, property_type: searchParams.get("type") || "" });
  const [priceRange, setPriceRange] = useState([0, PRICE_MAX]);
  const [surfaceRange, setSurfaceRange] = useState([0, SURFACE_MAX]);

  useEffect(() => {
    const type = searchParams.get("type") || "";
    const next = { ...filters, property_type: type };
    setFilters(next);
    fetchProperties(next);
    axios.get(`${API}/properties/cities`, { params: type ? { property_type: type } : {} })
      .then((r) => setCities(r.data))
      .catch(() => setCities([]));
  }, [searchParams]);

  const fetchProperties = async (activeFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilters.property_type) params.append("property_type", activeFilters.property_type);
      if (activeFilters.category) params.append("category", activeFilters.category);
      if (activeFilters.city) params.append("city", activeFilters.city);
      if (activeFilters.min_price > 0) params.append("min_price", activeFilters.min_price);
      if (activeFilters.max_price < PRICE_MAX) params.append("max_price", activeFilters.max_price);
      if (activeFilters.min_surface > 0) params.append("min_surface", activeFilters.min_surface);
      if (activeFilters.max_surface < SURFACE_MAX) params.append("max_surface", activeFilters.max_surface);
      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    fetchProperties(next);
  };

  const setType = (type) => {
    setSearchParams(type ? { type } : {});
  };

  const applyRanges = () => {
    const next = { ...filters, min_price: priceRange[0], max_price: priceRange[1], min_surface: surfaceRange[0], max_surface: surfaceRange[1] };
    setFilters(next);
    fetchProperties(next);
  };

  const resetFilters = () => {
    const next = { ...DEFAULT_FILTERS, property_type: filters.property_type };
    setFilters(next);
    setPriceRange([0, PRICE_MAX]);
    setSurfaceRange([0, SURFACE_MAX]);
    fetchProperties(next);
  };

  const hasActiveFilters = filters.category || filters.city || filters.min_price > 0 || filters.max_price < PRICE_MAX || filters.min_surface > 0 || filters.max_surface < SURFACE_MAX;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };
  
  return (
    <div className="min-h-screen bg-[#F9F9F9]" data-testid="catalog-page">
      <Header />
      
      {/* Page Header */}
      <section className="bg-white border-b border-gray-100 py-12">
        <div className="container-custom">
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-[#0A0A0A] mb-4">
            {filters.property_type === 'vente' ? 'Biens à vendre' : 
             filters.property_type === 'location' ? 'Biens à louer' : 
             filters.property_type === 'vendu' ? 'Biens vendus' :
             filters.property_type === 'loue' ? 'Biens loués' :
             'Tous nos biens'}
          </h1>
          <p className="text-gray-600">
            {properties.length} bien{properties.length > 1 ? 's' : ''} disponible{properties.length > 1 ? 's' : ''}
          </p>
        </div>
      </section>
      
      {/* Filters Bar */}
      <section className="bg-white border-b border-gray-100 sticky top-[72px] z-30">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              {/* Quick filters */}
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant={filters.property_type === '' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setType('')}
                  className={filters.property_type === '' ? 'bg-[#0A0A0A] text-[#D4AF37]' : ''}
                  data-testid="filter-all"
                >
                  Tous
                </Button>
                <Button
                  variant={filters.property_type === 'vente' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setType('vente')}
                  className={filters.property_type === 'vente' ? 'bg-[#0A0A0A] text-[#D4AF37]' : ''}
                  data-testid="filter-vente"
                >
                  À vendre
                </Button>
                <Button
                  variant={filters.property_type === 'location' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setType('location')}
                  className={filters.property_type === 'location' ? 'bg-[#0A0A0A] text-[#D4AF37]' : ''}
                  data-testid="filter-location"
                >
                  À louer
                </Button>
                <Button
                  variant={filters.property_type === 'vendu' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setType('vendu')}
                  className={filters.property_type === 'vendu' ? 'bg-green-600 text-white' : ''}
                  data-testid="filter-vendu"
                >
                  Vendu
                </Button>
                <Button
                  variant={filters.property_type === 'loue' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setType('loue')}
                  className={filters.property_type === 'loue' ? 'bg-purple-600 text-white' : ''}
                  data-testid="filter-loue"
                >
                  Loué
                </Button>
              </div>
              
            </div>
            
            <div className="flex items-center gap-2">
              {/* View mode */}
              <div className="hidden sm:flex items-center border rounded-md">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-[#0A0A0A] text-white' : 'text-gray-500'}`}
                  data-testid="view-grid"
                >
                  <Grid3X3 size={18} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === 'list' ? 'bg-[#0A0A0A] text-white' : 'text-gray-500'}`}
                  data-testid="view-list"
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Filters Panel */}
      <section className="bg-white border-b border-gray-100 py-6" data-testid="filters-panel">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500"><MapPin size={14} className="text-[#D4AF37]" /> Localisation</Label>
              <Select value={filters.city || "all"} onValueChange={(v) => updateFilter("city", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-city"><SelectValue placeholder="Toutes les communes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les communes</SelectItem>
                  {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500"><Home size={14} className="text-[#D4AF37]" /> Type de bien</Label>
              <Select value={filters.category || "all"} onValueChange={(v) => updateFilter("category", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Tous les types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500"><Wallet size={14} className="text-[#D4AF37]" /> Prix : {formatPrice(priceRange[0])} – {formatPrice(priceRange[1])}</Label>
              <Slider
                value={priceRange}
                onValueChange={setPriceRange}
                min={0}
                max={PRICE_MAX}
                step={10000}
                className="mt-2"
                data-testid="filter-price-slider"
              />
            </div>

            <div className="space-y-4">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500"><Ruler size={14} className="text-[#D4AF37]" /> Surface : {surfaceRange[0]} – {surfaceRange[1]} m²</Label>
              <Slider
                value={surfaceRange}
                onValueChange={setSurfaceRange}
                min={0}
                max={SURFACE_MAX}
                step={10}
                className="mt-2"
                data-testid="filter-surface-slider"
              />
            </div>

            <div className="flex items-end gap-2">
              <Button
                onClick={applyRanges}
                className="flex-1 bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
                data-testid="apply-filters-button"
              >
                Appliquer
              </Button>
              <Button
                variant="outline"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                data-testid="reset-filters-button"
              >
                <X size={18} />
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Properties Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className={`grid gap-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-sm overflow-hidden">
                  <div className="skeleton h-64" />
                  <div className="p-5 space-y-3">
                    <div className="skeleton h-6 w-3/4" />
                    <div className="skeleton h-4 w-1/2" />
                    <div className="skeleton h-8 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20" data-testid="no-results">
              <p className="text-gray-500 text-lg mb-4">Aucun bien ne correspond à vos critères</p>
              <Button variant="outline" onClick={resetFilters}>
                Réinitialiser les filtres
              </Button>
            </div>
          ) : (
            <div 
              className={`grid gap-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}
              data-testid="properties-grid"
            >
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CatalogPage;
